import { HStack, Stack, Text, VStack } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { TrackedButton } from '$/components/common/Button/TrackedButton';
import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import {
  HookFormCheckbox,
  type HookFormCheckboxProps,
} from '$/components/core/Form/HookFormCheckbox';
import { StyledLink } from '$/components/core/StyledLink';
import { useCloudMessageTranslation } from '$/hooks/useCloudMessageTranslation';
import { consentTerms } from '$/services/usecases/authentication';
import { useFavoriteStore } from '$/stores/useFavoriteStore';
import { useProjectStore } from '$/stores/useProjectStore';

export type ConsentData = {
  regToken: string;
  acceptedTerms: boolean;
};

const ConsentCheckbox = (props: HookFormCheckboxProps<ConsentData>) => (
  <HookFormCheckbox<ConsentData> {...props} />
);

export const SapCxConsentForm = () => {
  const { t } = useTranslation();

  const formMethods = useForm<ConsentData>({
    mode: 'onTouched',
    delayError: 100,
  });

  const [error, setError] = useState<string | null>(null);

  const authenticate = useAuthenticationStore.useAuthenticate();
  const setAuthModalType = useAuthenticationStore.useSetModalType();
  const closeModal = useAuthenticationStore.useOnModalClose();
  const regToken = useAuthenticationStore.usePendingAuthToken();

  const { cloudMessageTranslation } = useCloudMessageTranslation();

  const { mutate: onConsentTerms, isPending } = useMutation({
    mutationFn: consentTerms,
    onSuccess: async (data) => {
      const { response, isSuccessful } = data;

      if (
        isSuccessful &&
        response.user != null &&
        response.accessToken != null
      ) {
        authenticate(response.user, response.accessToken);
        await useProjectStore.getState().refetchProjects();
        await useFavoriteStore.getState().getFavorites();
        closeModal();
      } else if (isSuccessful) {
        setAuthModalType('registerSuccess');
      } else if (response.message === 'emailNotVerified') {
        setAuthModalType('registerOtp');
      } else {
        setError(cloudMessageTranslation(response.message));
      }
    },
    onError: () => {
      setError(cloudMessageTranslation('genericError'));
    },
  });

  return (
    <FormProvider {...formMethods}>
      <VStack
        as='form'
        gap='10'
        onSubmit={formMethods.handleSubmit((data) =>
          onConsentTerms({ ...data, regToken }),
        )}
      >
        <Stack gap='6'>
          <Text fontSize='sm'>
            <Trans
              i18nKey='dashboard.authentication.sapCxConsent.modalBody'
              shouldUnescape
            />
          </Text>
          <HStack alignItems='flex-start' gap='3'>
            <ConsentCheckbox
              accessor='acceptedTerms'
              registerOptions={{ validate: (val) => val === true }}
              customLabelContent={
                <Text mt='-1' fontSize='xs'>
                  <Trans
                    i18nKey='dashboard.authentication.createAccountLabel'
                    components={{
                      TermsLink: (
                        <StyledLink
                          color='primaryButton.background'
                          textDecoration='underline'
                          to='/tos'
                        >
                          Link
                        </StyledLink>
                      ),
                    }}
                  />
                </Text>
              }
            />
          </HStack>
          <Text fontSize='xs'>
            <Trans
              i18nKey='dashboard.authentication.createAccountLabelPrivacy'
              components={{
                PrivacyLink: (
                  <StyledLink
                    color='primaryButton.background'
                    textDecoration='underline'
                    to='/privacy'
                  >
                    Link
                  </StyledLink>
                ),
              }}
            />
          </Text>
        </Stack>
        {error && (
          <Text color='form.errorText' fontWeight='bold'>
            {error}
          </Text>
        )}
        <Stack gap='6' w='full'>
          <TrackedButton
            fontSize='sm'
            isDisabled={!formMethods.formState.isValid}
            isLoading={isPending}
            type='submit'
            contentName='CloudAccount'
            contentPiece='Registration'
            contentTarget='NewAccount'
          >
            {t('general.accept')}
          </TrackedButton>
        </Stack>
      </VStack>
    </FormProvider>
  );
};
